import { createSlice } from '@reduxjs/toolkit';
import { format, sub } from 'date-fns';
import { DiagnosticsAssetComponent } from '@/types/api/diagnostics.types';
import { ASSET_STUDIO } from '@/types/shared/navigation-tabs.enums';
import { DIAGNOSTICS_REGULAR_DATE_FORMAT, SENSORS_DATA_TYPE } from '@/const';

interface assetsStudioState {
  dateRange: {
    from: string;
    to: string;
  };
  alarmsDateRange: {
    from: string;
    to: string;
  };

  selectedComponents: {
    id: string;
    data: DiagnosticsAssetComponent[];
  };
  diagnostics: {
    alarmChartcriticality: ('critical' | 'non_critical')[];
  };
  selectedComponentObj?: any;
  selectedFormula?: any;
  selectedDiagnostic?: any;
  visualAIActiveItemIndex: number;
  visualAIEvidenceItems: any[];
  visualAIPlaylistItems: any;
  beltDefectsSelectedDate?: string;
  beltDefectsSelectedDefectHealth?: string;
  activeSensorDataInnerTab: string;
  sensorDataPreferences: {
    isFullPullEnabled: boolean;
    isOilReportsEnabled: boolean;
    isMaintenanceEnabled: boolean;
    isAlarmEnabled: boolean;
  };
  hasOilReport: boolean;
  useFaultsField: boolean; // Toggle for using new faults field vs old diagnostics
}
const getTodayAtMidnight = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const initialState: assetsStudioState = {
  dateRange: {
    from: format(sub(getTodayAtMidnight(), { months: 2 }), DIAGNOSTICS_REGULAR_DATE_FORMAT),
    to: format(new Date(), DIAGNOSTICS_REGULAR_DATE_FORMAT),
  },
  alarmsDateRange: {
    from: format(sub(getTodayAtMidnight(), { days: 8 }), DIAGNOSTICS_REGULAR_DATE_FORMAT),
    to: format(sub(getTodayAtMidnight(), { days: 1 }), DIAGNOSTICS_REGULAR_DATE_FORMAT),
  },
  diagnostics: {
    alarmChartcriticality: ['critical', 'non_critical'],
  },
  selectedComponents: { id: ASSET_STUDIO.ALL_COMPONENTS, data: [] },
  visualAIActiveItemIndex: -1,
  visualAIPlaylistItems: [],
  visualAIEvidenceItems: [],
  activeSensorDataInnerTab: SENSORS_DATA_TYPE.PI,
  sensorDataPreferences: {
    isFullPullEnabled: false,
    isOilReportsEnabled: true,
    isMaintenanceEnabled: true,
    isAlarmEnabled: true,
  },
  hasOilReport: false,
  useFaultsField: true, // Default to faults mode
};

const assetsStudioSlice = createSlice({
  initialState,
  name: 'assetsStudio',
  reducers: {
    setDateRange(state, action) {
      const { from, to } = action.payload;
      if (from && to)
        state.dateRange = {
          from: format(new Date(from), DIAGNOSTICS_REGULAR_DATE_FORMAT),
          to: format(new Date(to), DIAGNOSTICS_REGULAR_DATE_FORMAT),
        };
    },
    setAlarmsDateRange(state, action) {
      const { from, to } = action.payload;
      if (from && to)
        state.alarmsDateRange = {
          from: format(new Date(from), DIAGNOSTICS_REGULAR_DATE_FORMAT),
          to: format(new Date(to), DIAGNOSTICS_REGULAR_DATE_FORMAT),
        };
    },
    setSelectedComponents(state, action) {
      state.selectedComponents = { ...action.payload };
    },
    setSelectedFormula(state, action) {
      state.selectedFormula = { ...action.payload };
    },
    setSelectedDiagnostic(state, action) {
      state.selectedDiagnostic = action.payload;
    },
    setSelectedComponentObj(state, action) {
      state.selectedComponentObj = action.payload;
    },
    setVisualAIActiveItemIndex(state, action) {
      state.visualAIActiveItemIndex = action.payload;
    },
    setVisualAIPlaylistItems(state, action) {
      state.visualAIPlaylistItems = action.payload;
    },
    setBeltDefectsSelectedDate(state, action) {
      state.beltDefectsSelectedDate = action.payload;
    },
    setBeltDefectsSelectedDefectHealth(state, action) {
      state.beltDefectsSelectedDefectHealth = action.payload;
    },
    setActiveSensorDataInnerTab: (state, action) => {
      state.activeSensorDataInnerTab = action.payload;
    },
    setSensorDataPreferences(state, action) {
      state.sensorDataPreferences = { ...state.sensorDataPreferences, ...action.payload };
    },
    setHasOilReport(state, action) {
      state.hasOilReport = action.payload;
    },
    setDiangnosticsAlarmChartSeverity(state, action) {
      state.diagnostics.alarmChartcriticality = action.payload;
    },
    toggleUseFaultsField(state) {
      state.useFaultsField = !state.useFaultsField;
    },
    setUseFaultsField(state, action) {
      state.useFaultsField = action.payload;
    },
  },
});

export const AssetsStudioSliceReducer = assetsStudioSlice.reducer;
export const {
  setDateRange,
  setSelectedComponents,
  setSelectedDiagnostic,
  setSelectedFormula,
  setSelectedComponentObj,
  setVisualAIActiveItemIndex,
  setVisualAIPlaylistItems,
  setBeltDefectsSelectedDate,
  setBeltDefectsSelectedDefectHealth,
  setActiveSensorDataInnerTab,
  setSensorDataPreferences,
  setHasOilReport,
  setAlarmsDateRange,
  setDiangnosticsAlarmChartSeverity,
  toggleUseFaultsField,
  setUseFaultsField,
} = assetsStudioSlice.actions;
